// Rotating a degree-2 SH light between frames.
//
// A bundle is TRAINED in its capture frame — myra's is "up is -Y, front is +X"
// (aos-texavatars CLAUDE.md) — and `scene.json` states the offending orientation
// as `world_rotation`, which render/worldRotation.ts undoes on the SplatMesh. The
// PBR shade is the THIRD consumer of that frame, after the mesh and the plücker
// camera, and it is the one that reads a LIGHT: it evaluates world-space normals
// against `pbr.env.sh27`, which is authored in the training frame. Rotating one
// and not the other is the fault worldRotation.ts's own header names — "no error,
// just wrong colour".
//
// EXACT, not a quadrature. A rotation maps each SH band onto itself, so the
// rotated function lies exactly in the span of the same nine basis functions and
// a least-squares fit over enough directions recovers it to machine precision.
// Measured against `L'(d) == L(R^T d)` over 2003 probe directions, max error:
//
//   fit directions   16        32        64
//   error            1.4e-15   9.4e-16   1.1e-15      (cond(B) 1.24 / 1.05 / 1.01)
//
// A Fibonacci QUADRATURE of the same integral is not exact and converges at
// O(1/N) — 6.3e-3 at N=16, still 1.6e-4 at N=1024 — so the fit is what is used.

const FIT_DIRECTIONS = 32;

/** Row-major 3x3. */
export type Mat3 = ArrayLike<number>;

/** three.js SH order: [1, y, z, x, xy, yz, 3z²-1, xz, x²-y²] with its constants —
 *  the basis `shRadianceInto` (src/lib/envSamplers.js) evaluates, coefficient for
 *  coefficient, and the one `pbr/neural_bsdf.py::_sh_basis` projects onto. */
function basisInto(out: Float64Array, at: number, x: number, y: number, z: number): void {
  out[at] = 0.282095;
  out[at + 1] = 0.488603 * y;
  out[at + 2] = 0.488603 * z;
  out[at + 3] = 0.488603 * x;
  out[at + 4] = 1.092548 * x * y;
  out[at + 5] = 1.092548 * y * z;
  out[at + 6] = 0.315392 * (3 * z * z - 1);
  out[at + 7] = 1.092548 * x * z;
  out[at + 8] = 0.546274 * (x * x - y * y);
}

/** A Fibonacci sphere — near-uniform, so the fit stays well conditioned. */
function fitDirections(): Float64Array {
  const dirs = new Float64Array(FIT_DIRECTIONS * 3);
  const golden = Math.PI * (1 + Math.sqrt(5));
  for (let i = 0; i < FIT_DIRECTIONS; i++) {
    const offset = i + 0.5;
    const polar = Math.acos(1 - (2 * offset) / FIT_DIRECTIONS);
    const azimuth = golden * offset;
    dirs[i * 3] = Math.cos(azimuth) * Math.sin(polar);
    dirs[i * 3 + 1] = Math.sin(azimuth) * Math.sin(polar);
    dirs[i * 3 + 2] = Math.cos(polar);
  }
  return dirs;
}

/** Gauss-Jordan inverse of a small dense matrix, in place. Hand-rolled for the
 *  same reason `fitSimilarityRows` solves its own Kabsch: one 9x9, once. */
function invertInPlace(m: Float64Array, n: number): void {
  const inv = new Float64Array(n * n);
  for (let i = 0; i < n; i++) inv[i * n + i] = 1;
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(m[row * n + col]) > Math.abs(m[pivot * n + col])) pivot = row;
    }
    if (Math.abs(m[pivot * n + col]) < 1e-12) throw new Error("sh27Rotate: singular fit matrix");
    if (pivot !== col) {
      for (let k = 0; k < n; k++) {
        const a = m[col * n + k]; m[col * n + k] = m[pivot * n + k]; m[pivot * n + k] = a;
        const b = inv[col * n + k]; inv[col * n + k] = inv[pivot * n + k]; inv[pivot * n + k] = b;
      }
    }
    const scale = 1 / m[col * n + col];
    for (let k = 0; k < n; k++) { m[col * n + k] *= scale; inv[col * n + k] *= scale; }
    for (let row = 0; row < n; row++) {
      if (row === col) continue;
      const factor = m[row * n + col];
      if (factor === 0) continue;
      for (let k = 0; k < n; k++) {
        m[row * n + k] -= factor * m[col * n + k];
        inv[row * n + k] -= factor * inv[col * n + k];
      }
    }
  }
  m.set(inv);
}

/** (BᵀB)⁻¹Bᵀ for the fixed direction set — 9 x FIT_DIRECTIONS, built once. */
let fitPseudoInverse: Float64Array | null = null;
let fitBasisAt: Float64Array | null = null;

function ensureFit(): { pinv: Float64Array; dirs: Float64Array } {
  const dirs = fitBasisAt ?? fitDirections();
  fitBasisAt = dirs;
  if (fitPseudoInverse) return { pinv: fitPseudoInverse, dirs };
  const b = new Float64Array(FIT_DIRECTIONS * 9);
  for (let i = 0; i < FIT_DIRECTIONS; i++) {
    basisInto(b, i * 9, dirs[i * 3], dirs[i * 3 + 1], dirs[i * 3 + 2]);
  }
  const gram = new Float64Array(81);
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      let sum = 0;
      for (let i = 0; i < FIT_DIRECTIONS; i++) sum += b[i * 9 + r] * b[i * 9 + c];
      gram[r * 9 + c] = sum;
    }
  }
  invertInPlace(gram, 9);
  const pinv = new Float64Array(9 * FIT_DIRECTIONS);
  for (let r = 0; r < 9; r++) {
    for (let i = 0; i < FIT_DIRECTIONS; i++) {
      let sum = 0;
      for (let k = 0; k < 9; k++) sum += gram[r * 9 + k] * b[i * 9 + k];
      pinv[r * FIT_DIRECTIONS + i] = sum;
    }
  }
  fitPseudoInverse = pinv;
  return { pinv, dirs };
}

/** The 9x9 (row-major) that takes a coefficient set into the frame `rot` maps
 *  INTO: the rotated light satisfies `L'(d) = L(Rᵀd)`, so passing the bundle's
 *  `world_rotation` turns a training-frame light into a world-frame one, and
 *  passing its transpose turns a world-frame light back. */
export function sh27RotationMatrix(rot: Mat3): Float64Array {
  const { pinv, dirs } = ensureFit();
  const rotated = new Float64Array(FIT_DIRECTIONS * 9);
  for (let i = 0; i < FIT_DIRECTIONS; i++) {
    const x = Number(dirs[i * 3]), y = Number(dirs[i * 3 + 1]), z = Number(dirs[i * 3 + 2]);
    // Rᵀd for a row-major R: the COLUMNS of R dotted with d.
    basisInto(rotated, i * 9,
      Number(rot[0]) * x + Number(rot[3]) * y + Number(rot[6]) * z,
      Number(rot[1]) * x + Number(rot[4]) * y + Number(rot[7]) * z,
      Number(rot[2]) * x + Number(rot[5]) * y + Number(rot[8]) * z);
  }
  const out = new Float64Array(81);
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      let sum = 0;
      for (let i = 0; i < FIT_DIRECTIONS; i++) sum += pinv[r * FIT_DIRECTIONS + i] * rotated[i * 9 + c];
      out[r * 9 + c] = sum;
    }
  }
  return out;
}

/** Apply `matrix` (from sh27RotationMatrix) to an interleaved 9x3 coefficient
 *  set. Each channel rotates independently — the light is three scalar fields. */
export function rotateSh27(
  out: Float32Array, sh27: ArrayLike<number>, matrix: ArrayLike<number>,
): Float32Array {
  for (let coeff = 0; coeff < 9; coeff++) {
    for (let channel = 0; channel < 3; channel++) {
      let sum = 0;
      for (let k = 0; k < 9; k++) sum += Number(matrix[coeff * 9 + k]) * Number(sh27[k * 3 + channel]);
      out[coeff * 3 + channel] = sum;
    }
  }
  return out;
}

/** A unit quaternion as the row-major 3x3 this module takes. */
export function quaternionToMat3(q: { x: number; y: number; z: number; w: number }): Float64Array {
  const { x, y, z, w } = q;
  return Float64Array.from([
    1 - 2 * (y * y + z * z), 2 * (x * y - z * w), 2 * (x * z + y * w),
    2 * (x * y + z * w), 1 - 2 * (x * x + z * z), 2 * (y * z - x * w),
    2 * (x * z - y * w), 2 * (y * z + x * w), 1 - 2 * (x * x + y * y),
  ]);
}
